import { TabPanel, TabView } from "primereact/tabview";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { JobForm } from "./JobForm/JobForm";

import classes from "./CreateJobAndSchedule.module.scss";
import { Loader } from "../../../../components/Loader/Loader";
import { useJobAndScheduleForm } from "./JobAndScheduleForm.hook";
import { ScheduleForm } from "./ScheduleForm/ScheduleForm";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  JobFrequencyEnum,
  TJobFormData,
  isDayPatternInUse,
  resolveTimeZone,
} from "./CreateJobAndSchedule.model";
import { clearJobDraft, loadJobDraft, saveJobDraft } from "./job-draft";
import {
  JobDetailFields,
  PatternSaveCounts,
  OccurrenceTerm,
  SeriesTerms,
  changedOccurrenceTerms,
  describeJobDetailSaveError,
  describePatternSaveResult,
  describeSeriesSaveError,
  describeSkippedPatternDays,
  hasJobDetailChanged,
  hasSeriesChanged,
  isEndDateShortened,
  pickJobDetailFields,
} from "./day-pattern-save";
import {
  ConflictCheckResult,
  canPreflightConflicts,
  GroupedConflict,
  ScheduleConflict,
  groupScheduleConflicts,
  hasBlockingConflict,
} from "./schedule-conflicts";
import { buildCreateJobPayload } from "./create-job-payload";
import { requests } from "../../../../services/http-common";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../../Providers/ToastProvider";
import { DCSQueryClient } from "../../../../queryClient";

/**
 * Long enough that typing a job name is one request rather than fifteen, short
 * enough that the warning is on screen before the scheduler reaches for Save.
 */
const PREFLIGHT_DEBOUNCE_MS = 500;

/** Everything `PUT /job/:id/series` can change, as the form holds it. */
const seriesTermsOf = (values: Partial<TJobFormData>): SeriesTerms => ({
  dayPattern: values.dayPattern ?? [],
  recurEvery: values.recurEvery ?? null,
  recurrenceEndDate: values.recurrenceEndDate ?? null,
  // `?? []` and not `?? null`: an empty supervisor list is a value the user can
  // produce - it is the control cleared - and it means "the Service Division's
  // own". Collapsing it to null would make clearing the control indistinguishable
  // from never having loaded one.
  supervisorIds: values.supervisorIds ?? [],
  zoneCode: values.zoneCode ?? null,
  lateInAlertTime: values.lateInAlertTime ?? null,
  lateOutAlertTime: values.lateOutAlertTime ?? null,
});

/** Stands in for a series that never loaded, so nothing reads as shortened. */
const emptySeries: SeriesTerms = {
  dayPattern: [],
  recurEvery: null,
  recurrenceEndDate: null,
  supervisorIds: [],
  zoneCode: null,
  lateInAlertTime: null,
  lateOutAlertTime: null,
};

export const CreateJobAndSchedule = () => {
  const { show } = useToast();
  const [activeIndex, setActiveIndex] = useState(0);
  const [draft, setDraft] = useState<Partial<TJobFormData> | null>(null);

  // The watched leadId below is only filled in once the lead query resolves, so the
  // route param is the only lead id that is reliable on first render.
  const { leadId: routeLeadId } = useParams<{ leadId: string }>();

  const {
    form,
    isLeadDetailsLoading,
    isLeadDetailsApplied,
    jobId,
    isJobLoading,
    jobLoadProblem,
    loadedSeries,
    setLoadedSeries,
    loadedJobDetail,
    setLoadedJobDetail,
    isSeriesEditable,
  } = useJobAndScheduleForm();
  // The edit route carries a job id; the create route does not.
  const isEditingSeries = Boolean(jobId);
  const navigate = useNavigate();

  const { trigger, handleSubmit, watch, getValues, reset } = form;

  const createJob = useMutation({
    mutationFn: (newJob: any) => requests.post("/job", newJob),
  });

  /**
   * Regenerates the job's upcoming occurrences from the series' terms: the
   * weekday pattern, the recurrence interval and the end date.
   *
   * `recurEvery` and `jobEndTime` are OMITTED rather than sent as null when the
   * form has none. The endpoint reads an explicit null as "not supplied" and
   * leaves the stored value alone, so a null could never mean "clear it" - it
   * would only report a success that changed nothing.
   */
  const updateSeries = useMutation({
    mutationFn: (body: {
      dayPattern: TJobFormData["dayPattern"];
      timeZone: string;
      recurEvery?: number;
      jobEndTime?: string;
      // The four per-occurrence fields, each sent only when it moved - see
      // changedOccurrenceTerms for why an unconditional send is wrong rather
      // than merely wasteful. `supervisorIds: []` IS a value worth sending: it
      // asks for the Service Division's own.
      supervisorIds?: string[];
      zoneCode?: number;
      lateInAlertTime?: number;
      lateOutAlertTime?: number;
    }) => requests.put(`/job/${jobId}/series`, body),
  });

  /**
   * The job's own columns - name, notes and invoice generation.
   *
   * Only the three: `UpdateJobDetailDto` also takes `attachment2Path`,
   * `paymentsTerm` and `laborGoalHours`, and the service spreads the body
   * straight into the repository update, so anything sent here lands in the
   * database. The form's `attachment2Path` holds File objects, `paymentsTerm`
   * has no control on this screen, and the labour-hours number on screen comes
   * from the LEAD - `GET /job/:id` does not send the job's own - so writing it
   * back would overwrite the job with a figure the user never saw as its own.
   */
  const updateJobDetail = useMutation({
    mutationFn: (body: JobDetailFields) =>
      requests.put(`/job/jobDetail/${jobId}`, body),
  });

  // Saving uploads attachments before it POSTs the job, so it can run for
  // several seconds. Without this the Save button stayed live and inviting
  // throughout, and a second click raced the first - the first created the job
  // and the second came back "contract already has job".
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(loadJobDraft(routeLeadId));
  }, [routeLeadId]);

  useEffect(() => {
    if (jobLoadProblem) {
      show({ severity: "error", detail: jobLoadProblem });
    }
    // `show` is stable per provider; re-running on it would re-toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobLoadProblem]);

  /**
   * The body the preflight would ask about right now, serialized - or "" when
   * the form is not yet answerable.
   *
   * A string rather than an object because it is three things at once: the
   * debounce's input, react-query's cache key, and the test for whether the
   * conflicts on screen still describe what is in the form. An object would be
   * a fresh reference on every render and could serve as none of them.
   *
   * `watch()` with no argument, so every field is subscribed: the scheduling
   * inputs that change the answer are spread across the pattern cards, the
   * job-level times, the crew pickers and the recurrence fields, and a hand-kept
   * list of them is a list that goes stale.
   *
   * Built through the save's own builder with no attachment paths - see
   * buildCreateJobPayload on why the upload must not come with it.
   */
  const liveValues = watch();
  const preflightBody =
    // Create only. On the edit route the save goes through
    // `PUT /job/:id/series`, which excludes the job's own occurrences from
    // the check; posting this job's full create body to the preflight would
    // report its own existing shifts as overlaps and block its own save.
    !isEditingSeries && canPreflightConflicts(liveValues)
      ? JSON.stringify(buildCreateJobPayload(liveValues as TJobFormData, []))
      : "";

  /**
   * Debounces the BODY, not the request. Each keystroke replaces the pending
   * timer instead of queueing behind it, so a burst of edits produces one
   * request for the body the user stopped on - not one request per keystroke,
   * each merely late. The cleanup's clearTimeout is what makes that true.
   */
  const [debouncedBody, setDebouncedBody] = useState("");

  useEffect(() => {
    if (!preflightBody) {
      setDebouncedBody("");
      return;
    }

    const timer = setTimeout(() => setDebouncedBody(preflightBody), PREFLIGHT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [preflightBody]);

  const conflictsQuery = useQuery<ConflictCheckResult>({
    // Keyed by the body, which also means a slow answer to an abandoned body
    // cannot overwrite the answer to the current one.
    queryKey: ["job-conflicts", debouncedBody],
    queryFn: () => requests.post("/job/check-conflicts", JSON.parse(debouncedBody)),
    enabled: Boolean(debouncedBody),
    // A failed preflight is silent and must stay cheap: without this the app's
    // default three retries would re-ask on every 400 a mid-edit body earns.
    retry: false,
    refetchOnWindowFocus: false,
  });

  /**
   * Only conflicts that describe the body currently in the form. While the
   * debounce lags, `conflictsQuery.data` still answers the PREVIOUS body - and a
   * scheduler who has just fixed a clash and clicks Save inside that window must
   * not be refused for a clash that is no longer there. An in-flight or failed
   * preflight yields none, which is what keeps a failure from blocking: the save
   * itself still refuses a real overlap, with the server's own message.
   */
  const conflicts =
    debouncedBody && debouncedBody === preflightBody
      ? conflictsQuery.data?.conflicts ?? []
      : [];

  /**
   * What the server's 400 on `PUT /job/:id/series` said clashed. The edit route
   * has no preflight, so this is the only place its conflicts exist; it is
   * dropped as soon as the form changes (below) and on a successful save, and
   * never blocks Save itself - the server is the guard there.
   */
  const [seriesConflicts, setSeriesConflicts] = useState<ScheduleConflict[]>([]);
  const liveSnapshot = JSON.stringify(liveValues);

  useEffect(() => {
    // Same reference when already empty, so an ordinary keystroke costs no render.
    setSeriesConflicts((previous) => (previous.length ? [] : previous));
  }, [liveSnapshot]);

  const conflictGroups = groupScheduleConflicts([...conflicts, ...seriesConflicts]);
  // Only weekdays that really have a card get their lines beneath it; the rest
  // stay in the list above Save so nothing is hidden.
  const cardWeekdays = new Set<number>(
    isDayPatternInUse(liveValues) && liveValues.frequency === JobFrequencyEnum.WEEKLY
      ? (liveValues.dayPattern ?? []).map((entry) => entry.dayOfWeek)
      : []
  );
  const conflictsByWeekday: Record<number, GroupedConflict[]> = {};
  const unplacedConflicts: GroupedConflict[] = [];

  conflictGroups.forEach((group) => {
    if (cardWeekdays.has(group.weekday)) {
      (conflictsByWeekday[group.weekday] ??= []).push(group);
    } else {
      unplacedConflicts.push(group);
    }
  });

  if (isLeadDetailsLoading || isJobLoading) {
    return <Loader />;
  }

  const [attachment2Path, leadId] = watch(["attachment2Path", "leadId"]);

  const handleSave = async (formData: TJobFormData) => {
    if (isSaving) {
      return;
    }

    // Refused here rather than by disabling the button: the same button is
    // "Next" on the first tab, and a disabled Save would also hide this guard
    // from any test that only watches whether /job was posted.
    //
    // No toast. The lines this reads from are rendered immediately above the
    // button the user just pressed, and a form-level restatement of them is the
    // treatment the day-level validation already deliberately avoids.
    if (hasBlockingConflict(conflicts)) {
      return;
    }

    setIsSaving(true);

    // Which of the edit route's two requests the catch below is reporting. The
    // fallback messages differ, and a refused name edit announced as a failed
    // day-pattern update sends the user to the wrong control.
    let failedStage: "detail" | "series" = "detail";

    try {
      if (isEditingSeries) {
        // Detail first, series second, inside one guard and one try. The detail
        // update writes three columns; the series update deletes and recreates a
        // year of occurrences. Taking the cheap, reversible one first means a
        // series failure leaves the name edit stored and the retry idempotent -
        // the reverse order would throw the name edit away along with it.
        await saveJobDetail(formData);
        failedStage = "series";
        await saveSeries(formData);
        // The edit stays on the page, so the guard has to be lifted here - the
        // create branch navigates away instead.
        setSeriesConflicts([]);
        setIsSaving(false);
      } else {
        await saveJob(formData);
      }
    } catch (error: any) {
      const serverConflicts = error?.response?.data?.error?.conflicts;

      if (isEditingSeries && failedStage === "series" && Array.isArray(serverConflicts)) {
        setSeriesConflicts(serverConflicts);
      }
      show({
        severity: "error",
        detail: !isEditingSeries
          ? error.response?.data?.error?.message || "Something went wrong"
          : failedStage === "detail"
          ? describeJobDetailSaveError(error)
          : describeSeriesSaveError(error, {
              endDateShortened: isEndDateShortened(
                loadedSeries ?? emptySeries,
                seriesTermsOf(getValues())
              ),
            }),
      });
      // Only on failure: a success navigates away, and setting state on the
      // way out would fire after unmount.
      setIsSaving(false);
    }
  };

  /**
   * The job's own fields, when they changed. Silent when they did not: this runs
   * on every edit save, and "nothing to do" is not news.
   *
   * The baseline is advanced as soon as this succeeds, so a retry after a failed
   * series call does not write the same three columns a second time.
   */
  const saveJobDetail = async (formData: TJobFormData) => {
    // The labour-hours control writes to `leadlaborGoalHours`; the column it
    // ends up in is `laborGoalHours`.
    const current = pickJobDetailFields({
      ...formData,
      laborGoalHours: formData.leadlaborGoalHours,
    });

    if (!hasJobDetailChanged(loadedJobDetail ?? current, current)) {
      return;
    }

    await updateJobDetail.mutateAsync(current);
    setLoadedJobDetail(current);
    // Said separately from the series result, and before it: when the
    // regeneration then fails, this is what tells the user the name edit is
    // safe and does not need retyping.
    show({ detail: "Job details saved." });
  };

  /**
   * Inside handleSave's isSaving guard, not beside it: regenerating a year of
   * occurrences takes as long as creating a job did, and a second click would
   * send a second regeneration over the first one's half-written series.
   */
  const saveSeries = async (formData: TJobFormData) => {
    if (!isDayPatternInUse(formData)) {
      show({
        severity: "error",
        detail:
          "Only a weekly recurring job has a per-day schedule. Pick the weekdays before saving.",
      });
      return;
    }

    const terms = seriesTermsOf(formData);

    // The interval and the end date count as changes too: either one re-dates
    // every upcoming occurrence, and neither would be sent by a test that only
    // looked at the weekday cards.
    if (!hasSeriesChanged(loadedSeries ?? terms, terms)) {
      // Sent anyway, this would delete and recreate every upcoming shift to
      // arrive back where it started.
      show({ detail: "No changes to the day pattern." });
      return;
    }

    // Which of the four per-occurrence fields this save is actually changing.
    // Only those go up: the endpoint reads an omitted field as "leave the
    // series' own alone", and the values on screen came from one occurrence
    // which need not be the one the server inherits from.
    const changedTerms = changedOccurrenceTerms(loadedSeries ?? terms, terms);
    const sends = (term: OccurrenceTerm) => changedTerms.includes(term);

    const counts: Partial<PatternSaveCounts> =
      (await updateSeries.mutateAsync({
        dayPattern: formData.dayPattern,
        timeZone: resolveTimeZone(formData.timeZone),
        // Omitted, never null - see updateSeries.
        ...(typeof terms.recurEvery === "number" && { recurEvery: terms.recurEvery }),
        ...(terms.recurrenceEndDate && {
          jobEndTime: new Date(terms.recurrenceEndDate).toISOString(),
        }),
        // No truthiness test on the value: `[]` is the instruction "use the
        // Service Division's supervisors", and `&&` on the array would send it
        // anyway while `terms.supervisorIds &&` on an empty one still would -
        // the test that matters is whether the user changed it.
        ...(sends("supervisorIds") && { supervisorIds: terms.supervisorIds }),
        ...(sends("zoneCode") && { zoneCode: terms.zoneCode as number }),
        ...(sends("lateInAlertTime") && {
          lateInAlertTime: terms.lateInAlertTime as number,
        }),
        ...(sends("lateOutAlertTime") && {
          lateOutAlertTime: terms.lateOutAlertTime as number,
        }),
      })) ?? {};

    // The saved terms are the new baseline, or a second Save would regenerate
    // the series again for nothing.
    setLoadedSeries(terms);

    // One toast, not two. The regeneration can succeed and still have refused
    // part of what was asked for - a weekday whose kept occurrence holds the day
    // is dropped silently - and a green "updated" beside a separate warning
    // invites the scheduler to read the first and dismiss the second. The counts
    // and the refusals are one piece of news, so they share one message.
    //
    // A warning, and a long-lived one: the default four seconds is enough for
    // "saved", and not enough to read which days did not take and why. Still a
    // toast rather than a dialog or a banner, because the toast is the convention
    // every other outcome of this save already uses.
    const skippedDays = describeSkippedPatternDays(counts.skipped);

    show(
      skippedDays
        ? {
            severity: "warn",
            detail: `${describePatternSaveResult(counts)} ${skippedDays}`,
            life: 20000,
          }
        : { detail: describePatternSaveResult(counts) }
    );
  };

  const saveJob = async (formData: TJobFormData) => {
    let otherDocumentPaths = [];

    if (attachment2Path.length) {
      const otherDocPromices: any[] = [];
      attachment2Path.forEach((doc) => {
        if (doc.size) {
          const formData = new FormData();
          formData.append("attachment", doc);
          formData.append("leadId", `${leadId}`);

          otherDocPromices.push(
            requests.post("/job/attachment", formData, {
              headers: { "Content-Type": "multipart/form-data" },
              transformRequest: (formData: any) => formData,
            })
          );
        }
      });

      const otherDocPaths = await Promise.all(otherDocPromices);
      otherDocumentPaths = otherDocPaths;
    }
    const requestData = buildCreateJobPayload(formData, otherDocumentPaths);

    await createJob.mutateAsync(requestData);
    // The schedule calendar caches a payload per visible window, so its
    // cached windows all predate this job. Drop them rather than invalidate:
    // the calendar is unmounted here, and an invalidated window would still
    // be painted from cache - without the new job - on the way back to it.
    DCSQueryClient.removeQueries({ queryKey: ["jobs-data"] });
    clearJobDraft(routeLeadId);
    show({ detail: "Job was created successfully!" });
    navigate("/contracts");
  };

  const handleInvalidSubmit = () => {
    show({
      severity: "error",
      detail: "Please complete the required schedule fields before saving.",
    });
  };

  const handleNextClick = async () => {
    if (activeIndex === 0) {
      const isNameFilled = await trigger("name");
      if (!isNameFilled) {
        return;
      }
      setActiveIndex(1);
      return;
    }
    if (isSaving) {
      return;
    }

    handleSubmit(handleSave, handleInvalidSubmit)();
  };

  return (
    <div>
      <SectionHeader
        title={isEditingSeries ? "Edit Job & Schedule" : "Create Job & Schedule"}
      />
      {/*
        Offered only once the lead query has applied its own reset(), otherwise a
        restore could land first and be silently overwritten by it.
      */}
      {draft && isLeadDetailsApplied && !isEditingSeries ? (
        <div className={classes.draftBanner}>
          <span>
            Saved progress found for this lead. Attachments are not included and
            will need choosing again.
          </span>
          <Button
            type="button"
            label="Restore"
            className="p-button-sm"
            onClick={() => {
              reset((values) => ({ ...values, ...draft }));
              setDraft(null);
            }}
          />
          <Button
            type="button"
            label="Discard"
            className="p-button-sm p-button-text"
            onClick={() => {
              clearJobDraft(routeLeadId);
              setDraft(null);
            }}
          />
        </div>
      ) : null}
      <div className={classes.tabContent}>
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <TabPanel header="Job details">
            <JobForm form={form} isSeriesEdit={isEditingSeries} />
          </TabPanel>
          <TabPanel header="Schedule details">
            {/*
              isSeriesEdit, not isScheduleEdit: that flag belongs to
              ScheduleEdit, the per-occurrence page - which is exactly where the
              fields disabled here tell the user to go.
            */}
            <ScheduleForm
              form={form}
              isSeriesEdit={isEditingSeries}
              // Whether the series route can act on this job at all. Decided
              // from the job as it was LOADED, not from the form: see
              // useJobAndScheduleForm's isSeriesEditable.
              isSeriesEditable={Boolean(isSeriesEditable)}
              conflictsByWeekday={conflictsByWeekday}
            />
          </TabPanel>
        </TabView>
      </div>
      {/*
        One line per conflict, above the Save button it governs - the same
        treatment the per-day problems get, and for the same reason: a single
        form-level summary names nobody and nothing to change.
      */}
      {unplacedConflicts.length > 0 && (
        <ul className={classes.conflicts}>
          {unplacedConflicts.map((message) => (
            <li
              key={message.key}
              // p-error is the theme's own error class, which is what every
              // other message on this form uses. The warning amber is this
              // module's, matching the draft banner above - PrimeFlex is not
              // installed, so p-d-* and friends would be no-ops.
              className={
                message.severity === "error" ? "p-error" : classes.conflictWarning
              }
            >
              {message.text}
            </li>
          ))}
        </ul>
      )}
      <div className={classes.actions}>
        <Button
          label="Cancel"
          type="reset"
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate(-1)}
        />
        {/*
          Create only: a draft is keyed by lead and restored on the create
          route, where it would overwrite a loaded series' own pattern.
        */}
        {!isEditingSeries && (
          <Button
            type="button"
            label="Save progress"
            icon="pi pi-save"
            className="p-button-outlined p-button-secondary"
            onClick={() => {
              saveJobDraft(routeLeadId, getValues());
              show({ detail: "Progress saved on this browser." });
            }}
          />
        )}
        <Button
          label={!activeIndex ? "Next" : "Save"}
          onClick={handleNextClick}
          disabled={isSaving}
          loading={isSaving}
        />
      </div>
    </div>
  );
};
